import { companyRequest } from '@/company/api';

// The owner's public-link routes (src/apps/public/routes.mjs).
export type PublicLinkState = {
  artifactId: string;
  title: string | null;
  youOwnIt: boolean;
  /** The owner, or a company owner or admin (review M6). */
  canUnpublish: boolean;
  available: boolean;
  state: 'live' | 'unpublished' | 'taken_down';
  url: string | null;
  embedSnippet: string | null;
  embedOrigins: string[];
  versionId: string | null;
  selectedVersionId?: string;
  publishedAt: string | null;
  refreshedAt: string | null;
  showMark: boolean;
  exposes: {
    collections: {
      id: string;
      label: string | null;
      fields: string[];
      filtered: boolean;
      rows: number | null;
    }[];
    actions: {
      id: string;
      label: string | null;
      collectionId: string;
      collectionLabel: string | null;
      fields: string[];
      /** The functions a submission starts (row events on its table); `on` = turned on now. */
      runs?: { eventId: string; function: string; on: boolean }[];
    }[];
  } | null;
  takenDown: { at: string; reason: string | null } | null;
};
export type PublicLinkSelection = { artifactId: string; versionId: string; revisionId: string };

// Anything that is not a public-link answer (an older server, another route's
// fixture) reads as "not available", so the sheet shows no section.
const UNAVAILABLE: PublicLinkState = {
  artifactId: '',
  title: null,
  youOwnIt: false,
  canUnpublish: false,
  available: false,
  state: 'unpublished',
  url: null,
  embedSnippet: null,
  embedOrigins: [],
  versionId: null,
  publishedAt: null,
  refreshedAt: null,
  showMark: true,
  exposes: null,
  takenDown: null,
};
export function readPublicLinkState(value: unknown): PublicLinkState {
  const raw = (value && typeof value === 'object' ? value : {}) as Partial<PublicLinkState>;
  if (raw.available !== true) return UNAVAILABLE;
  return {
    ...UNAVAILABLE,
    ...raw,
    embedOrigins: Array.isArray(raw.embedOrigins) ? raw.embedOrigins : [],
    canUnpublish: raw.canUnpublish === true,
    exposes: raw.exposes ?? null,
  };
}
const answered = (request: Promise<unknown>) => request.then(readPublicLinkState);

const base = (artifactId: string) =>
  `/api/v1/company/apps/${encodeURIComponent(artifactId)}/public`;

export const publicLinkApi = {
  read: (selection: PublicLinkSelection, session?: typeof fetch) =>
    answered(
      companyRequest<unknown>(
        `${base(selection.artifactId)}?${new URLSearchParams({
          versionId: selection.versionId,
          revisionId: selection.revisionId,
        })}`,
        'GET',
        undefined,
        session,
      ),
    ),
  publish: (selection: PublicLinkSelection, session?: typeof fetch) =>
    answered(
      companyRequest<unknown>(
        `${base(selection.artifactId)}/publish`,
        'POST',
        { versionId: selection.versionId, revisionId: selection.revisionId, confirmed: true },
        session,
      ),
    ),
  refresh: (artifactId: string, session?: typeof fetch) =>
    answered(companyRequest<unknown>(`${base(artifactId)}/refresh`, 'POST', {}, session)),
  unpublish: (artifactId: string, session?: typeof fetch) =>
    answered(companyRequest<unknown>(`${base(artifactId)}/unpublish`, 'POST', {}, session)),
  embed: (artifactId: string, embedOrigins: string[], session?: typeof fetch) =>
    answered(
      companyRequest<unknown>(`${base(artifactId)}/embed`, 'POST', { embedOrigins }, session),
    ),
};

/** The sites an owner typed, one per line or separated by commas or spaces. */
export function readSites(value: string): string[] {
  return value
    .split(/[\s,]+/u)
    .map((item) => item.trim())
    .filter(Boolean);
}
